import { useCallback, useEffect, useMemo, useState } from 'react';
import { apiService } from '../services/api.service';

type Branch = { id: string; code: string; name: string };
type PreviewRow = {
  installmentId: string;
  loanId?: string;
  loanNumber: string | null;
  installmentNumber: number;
  amount: number;
  dueDate: string;
  cuil: string;
  cbu: string;
  customerName: string;
};
type ExcludedRow = {
  installmentId: string;
  reason: string;
  loanNumber?: string | null;
  installmentNumber?: number;
  dueDate?: string;
  amount?: number;
  customerName?: string;
  activeBatchNumber?: string | null;
  activeBatchStatus?: string | null;
};
type Preview = {
  eligibleCount: number;
  excludedCount: number;
  totalAmount: number;
  eligible: PreviewRow[];
  excluded: ExcludedRow[];
};
type BatchRow = {
  id: string;
  batchNumber: string;
  status: string;
  lineCount: number;
  totalAmount: number;
  sentAt?: string | null;
  branch?: Branch;
  hasExcel?: boolean;
  hasFixed?: boolean;
};

function todayIso() {
  const d = new Date();
  return d.toISOString().slice(0, 10);
}

function monthStartIso() {
  const d = new Date();
  return new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), 1)).toISOString().slice(0, 10);
}

function money(n: number) {
  return Number(n).toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function dateLabel(value: string | Date) {
  const s = typeof value === 'string' ? value.slice(0, 10) : value.toISOString().slice(0, 10);
  const [y, m, d] = s.split('-');
  return y && m && d ? `${d}/${m}/${y}` : s;
}

export function BaproDebitBatches() {
  const [branches, setBranches] = useState<Branch[]>([]);
  const [branchId, setBranchId] = useState('');
  const [dueFrom, setDueFrom] = useState(monthStartIso());
  const [dueTo, setDueTo] = useState(todayIso());
  const [preview, setPreview] = useState<Preview | null>(null);
  /** installmentIds incluidos en el lote (por defecto todos los elegibles del preview). */
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [batches, setBatches] = useState<BatchRow[]>([]);
  const [selected, setSelected] = useState<any>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);

  const [filterReview, setFilterReview] = useState(false);
  const [importSummary, setImportSummary] = useState<any | null>(null);

  const selectedRows = useMemo(() => {
    if (!preview?.eligible?.length) return [];
    return preview.eligible.filter((r) => selectedIds.has(r.installmentId));
  }, [preview, selectedIds]);

  const selectedTotal = useMemo(
    () => selectedRows.reduce((s, r) => s + Number(r.amount), 0),
    [selectedRows],
  );

  const loadBranches = useCallback(async () => {
    try {
      const list = (await apiService.getBranches()) as any;
      const rows: any[] = Array.isArray(list) ? list : Array.isArray(list?.data) ? list.data : [];
      setBranches(rows.map((b: any) => ({ id: b.id, code: b.code, name: b.name })));
      if (!branchId && rows[0]?.id) setBranchId(rows[0].id);
    } catch (e: any) {
      setError(e?.message ?? 'No se pudieron cargar sucursales');
    }
  }, [branchId]);

  const loadBatches = useCallback(async () => {
    try {
      const data = (await apiService.listBaproDebitBatches({
        branchId: branchId || undefined,
        needsReview: filterReview || undefined,
      })) as any;
      const rows: BatchRow[] = Array.isArray(data) ? data : Array.isArray(data?.data) ? data.data : [];
      setBatches(rows);
    } catch (e: any) {
      setError(e?.message ?? 'Error listando lotes');
    }
  }, [branchId, filterReview]);

  useEffect(() => {
    loadBranches();
  }, [loadBranches]);

  useEffect(() => {
    loadBatches();
  }, [loadBatches]);

  async function runPreview() {
    setBusy(true);
    setError(null);
    setMsg(null);
    try {
      const data = await apiService.previewBaproDebit({ branchId, dueFrom, dueTo });
      const p = (data?.data ?? data) as Preview;
      setPreview(p);
      setSelectedIds(new Set((p.eligible ?? []).map((r) => r.installmentId)));
    } catch (e: any) {
      setError(e?.response?.data?.message ?? e?.message ?? 'Error preview');
      setPreview(null);
      setSelectedIds(new Set());
    } finally {
      setBusy(false);
    }
  }

  async function createAndSend() {
    if (!selectedRows.length) {
      setError('Seleccioná al menos una cuota del preview para generar el lote');
      return;
    }
    const branchLabel =
      branches.find((b) => b.id === branchId)?.code ??
      branches.find((b) => b.id === branchId)?.name ??
      'sucursal';
    const ok = confirm(
      [
        '¿Generar Excel y enviar el lote BAPRO?',
        '',
        `Sucursal: ${branchLabel}`,
        `Vencimientos: ${dateLabel(dueFrom)} → ${dateLabel(dueTo)}`,
        `Cuotas a enviar: ${selectedRows.length}`,
        `Total: $${money(selectedTotal)}`,
        '',
        'Se va a crear el lote SENT y descargar Excel + EBT_.',
        'Revisá la tabla de seleccionadas arriba antes de confirmar.',
      ].join('\n'),
    );
    if (!ok) return;

    setBusy(true);
    setError(null);
    setMsg(null);
    try {
      const created = await apiService.createBaproDebitBatch({
        branchId,
        dueFrom,
        dueTo,
        installmentIds: selectedRows.map((r) => r.installmentId),
      });
      const batch = created?.data ?? created;
      const sent = await apiService.sendBaproDebitBatch(batch.id);
      setMsg(
        `Lote ${(sent?.data ?? sent).batchNumber} enviado (Excel + EBT_) · ${selectedRows.length} líneas · $${money(selectedTotal)}`,
      );
      setPreview(null);
      setSelectedIds(new Set());
      await loadBatches();
      await openBatch((sent?.data ?? sent).id);
    } catch (e: any) {
      setError(e?.response?.data?.message ?? e?.message ?? 'Error al enviar');
    } finally {
      setBusy(false);
    }
  }

  function toggleAll(checked: boolean) {
    if (!preview?.eligible) return;
    setSelectedIds(checked ? new Set(preview.eligible.map((r) => r.installmentId)) : new Set());
  }

  function toggleOne(id: string, checked: boolean) {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (checked) next.add(id);
      else next.delete(id);
      return next;
    });
  }

  async function openBatch(id: string) {
    setBusy(true);
    try {
      const data = await apiService.getBaproDebitBatch(id);
      setSelected(data?.data ?? data);
    } catch (e: any) {
      setError(e?.message ?? 'Error detalle');
    } finally {
      setBusy(false);
    }
  }

  async function releaseSelected() {
    if (!selected?.id) return;
    if (
      !confirm(
        `¿Liberar remanente SENT del lote ${selected.batchNumber}?\n\nLas cuotas vuelven a quedar disponibles para un nuevo preview/envío.`,
      )
    ) {
      return;
    }
    setBusy(true);
    try {
      const data = await apiService.releaseBaproDebitBatch(selected.id, {
        reason: 'Liberación manual remanente',
      });
      setSelected(data?.data ?? data);
      setMsg('Remanente liberado');
      await loadBatches();
    } catch (e: any) {
      setError(e?.message ?? 'Error liberación');
    } finally {
      setBusy(false);
    }
  }

  async function importResponseFiles(files: FileList | File[] | null) {
    if (!selected?.id || !files?.length) return;
    const list = Array.from(files);
    setBusy(true);
    setError(null);
    setImportSummary(null);
    const batchId = selected.id;
    try {
      let lastSummary: any = null;
      let closed = false;
      let totalOk = 0;
      let totalRej = 0;
      for (const file of list) {
        const data = await apiService.importBaproDebitResponse(batchId, file);
        const payload = data?.data ?? data;
        lastSummary = payload?.summary ?? payload;
        totalOk += Number(payload?.summary?.success ?? 0);
        totalRej += Number(payload?.summary?.rejected ?? 0);
        if (payload?.closed) closed = true;
      }
      setImportSummary(lastSummary);
      setMsg(
        closed
          ? `Import OK (${list.length} archivo/s). Lote cerrado · pagados ${totalOk}, rechazados ${totalRej}`
          : `Import OK (${list.length} archivo/s). Lote sigue abierto · pagados ${totalOk}, rechazados ${totalRej}. Podés seguir importando.`,
      );
      await openBatch(batchId);
      await loadBatches();
    } catch (e: any) {
      setError(e?.response?.data?.message ?? e?.message ?? 'Error import');
      await openBatch(batchId).catch(() => undefined);
      await loadBatches();
    } finally {
      setBusy(false);
    }
  }

  async function closeSelected() {
    if (!selected?.id) return;
    if (
      !confirm(
        `¿Cerrar el lote ${selected.batchNumber}?\n\nNo se podrán importar más respuestas ni registrar pagos BAPRO.\nLas cuotas aún SENT se liberan para un lote futuro.`,
      )
    ) {
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const data = await apiService.closeBaproDebitBatch(selected.id, {
        reason: 'Cierre manual de lote',
        releaseOpen: true,
      });
      setSelected(data?.data ?? data);
      setMsg('Lote cerrado');
      await loadBatches();
    } catch (e: any) {
      setError(e?.response?.data?.message ?? e?.message ?? 'Error al cerrar');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="p-6 space-y-6 max-w-6xl">
      <div>
        <h1 className="text-2xl font-semibold text-gray-900">Débito Provincia (BAPRO)</h1>
        <p className="text-sm text-gray-600 mt-1">
          Lotes Emp Pago Directo: enviás el Excel al banco y podés ir importando varias respuestas
          (cobros en distintos días). El lote se cierra solo cuando cobró el 100%, o a mano cuando
          confirmás el cierre.
        </p>
      </div>

      {error && (
        <div className="rounded border border-red-200 bg-red-50 text-red-800 px-3 py-2 text-sm">{error}</div>
      )}
      {msg && (
        <div className="rounded border border-emerald-200 bg-emerald-50 text-emerald-800 px-3 py-2 text-sm">
          {msg}
        </div>
      )}

      <section className="rounded-lg border bg-white p-4 space-y-3">
        <h2 className="font-medium text-gray-900">Nuevo lote</h2>
        <p className="text-xs text-slate-500">
          El preview lista <strong>cuotas del plan</strong> con vencimiento en el rango (no hace falta
          crear movimientos de payroll antes). Requiere CBU del cliente (ficha Cliente) y préstamo
          APPROVED/ACTIVE. Si ves 0 elegibles, abrí &quot;Ver exclusiones&quot;.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <label className="text-sm">
            Sucursal
            <select
              className="mt-1 w-full border rounded-md px-2 py-2"
              value={branchId}
              onChange={(e) => setBranchId(e.target.value)}
            >
              {branches.map((b) => (
                <option key={b.id} value={b.id}>
                  {b.code} — {b.name}
                </option>
              ))}
            </select>
          </label>
          <label className="text-sm">
            Vence desde
            <input
              type="date"
              className="mt-1 w-full border rounded-md px-2 py-2"
              value={dueFrom}
              onChange={(e) => setDueFrom(e.target.value)}
            />
          </label>
          <label className="text-sm">
            Vence hasta
            <input
              type="date"
              className="mt-1 w-full border rounded-md px-2 py-2"
              value={dueTo}
              onChange={(e) => setDueTo(e.target.value)}
            />
          </label>
          <div className="flex items-end gap-2">
            <button
              type="button"
              disabled={busy || !branchId}
              onClick={runPreview}
              className="px-3 py-2 rounded-md bg-slate-800 text-white text-sm disabled:opacity-50"
            >
              Preview
            </button>
            <button
              type="button"
              disabled={busy || selectedRows.length === 0}
              onClick={createAndSend}
              className="px-3 py-2 rounded-md bg-blue-600 text-white text-sm disabled:opacity-50"
              title="Genera el lote solo con las filas tildadas y descarga Excel + EBT_"
            >
              Generar Excel y enviar ({selectedRows.length})
            </button>
          </div>
        </div>
        {preview && (
          <div className="space-y-3 text-sm text-gray-700">
            <div className="flex flex-wrap gap-x-4 gap-y-1">
              <span>
                Elegibles API: <strong>{preview.eligibleCount}</strong>
              </span>
              <span>
                Excluidas: <strong>{preview.excludedCount}</strong>
              </span>
              <span>
                Seleccionadas: <strong>{selectedRows.length}</strong>
              </span>
              <span>
                Recaudación si pagan todas:{' '}
                <strong className="text-emerald-800">${money(selectedTotal)}</strong>
              </span>
            </div>
            {preview.eligibleCount === 0 && preview.excludedCount === 0 && (
              <p className="text-amber-800 text-xs">
                No hay cuotas PENDING/OVERDUE/PARTIAL con vencimiento en ese rango para la sucursal.
              </p>
            )}
            {(preview.eligible?.length ?? 0) > 0 && (
              <div className="border rounded-md overflow-hidden border-emerald-200">
                <div className="px-3 py-2 bg-emerald-50 border-b border-emerald-100 text-xs text-emerald-900 flex flex-wrap items-center justify-between gap-2">
                  <div>
                    <strong>A enviar si confirmás</strong>
                    {' · '}
                    {selectedRows.length} cuota{selectedRows.length === 1 ? '' : 's'} · $
                    {money(selectedTotal)}
                    <span className="block text-emerald-800/80 font-normal mt-0.5">
                      Desmarcá filas para sacarlas del Excel antes de generar.
                    </span>
                  </div>
                  <label className="inline-flex items-center gap-1.5 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={
                        preview.eligible.length > 0 &&
                        selectedIds.size === preview.eligible.length
                      }
                      onChange={(e) => toggleAll(e.target.checked)}
                    />
                    Todas
                  </label>
                </div>
                <div className="overflow-auto max-h-96">
                  <table className="min-w-full text-xs">
                    <thead className="bg-gray-50 sticky top-0 text-left">
                      <tr>
                        <th className="px-2 py-2 w-8" />
                        <th className="px-2 py-2">Cliente</th>
                        <th className="px-2 py-2">Crédito</th>
                        <th className="px-2 py-2">Cuota</th>
                        <th className="px-2 py-2">Vence</th>
                        <th className="px-2 py-2">CUIL</th>
                        <th className="px-2 py-2">CBU</th>
                        <th className="px-2 py-2 text-right">Importe</th>
                      </tr>
                    </thead>
                    <tbody>
                      {preview.eligible.map((r) => {
                        const checked = selectedIds.has(r.installmentId);
                        return (
                          <tr
                            key={r.installmentId}
                            className={`border-t ${checked ? '' : 'bg-slate-50 text-slate-400'}`}
                          >
                            <td className="px-2 py-1.5">
                              <input
                                type="checkbox"
                                checked={checked}
                                onChange={(e) => toggleOne(r.installmentId, e.target.checked)}
                              />
                            </td>
                            <td className="px-2 py-1.5 font-medium text-slate-800">{r.customerName}</td>
                            <td className="px-2 py-1.5 font-mono">{r.loanNumber ?? '—'}</td>
                            <td className="px-2 py-1.5">#{r.installmentNumber}</td>
                            <td className="px-2 py-1.5">{dateLabel(r.dueDate)}</td>
                            <td className="px-2 py-1.5 font-mono">{r.cuil}</td>
                            <td className="px-2 py-1.5 font-mono">
                              {r.cbu ? `…${String(r.cbu).slice(-4)}` : '—'}
                            </td>
                            <td className="px-2 py-1.5 text-right tabular-nums font-medium">
                              ${money(r.amount)}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                    <tfoot className="bg-emerald-50 border-t sticky bottom-0">
                      <tr>
                        <td colSpan={7} className="px-2 py-2 text-right font-medium text-emerald-900">
                          Total a enviar ({selectedRows.length} seleccionadas)
                        </td>
                        <td className="px-2 py-2 text-right font-semibold tabular-nums text-emerald-900">
                          ${money(selectedTotal)}
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </div>
            )}
            {preview.excluded.length > 0 && (
              <details className="border rounded-md border-amber-200 overflow-hidden" open={preview.eligibleCount === 0}>
                <summary className="cursor-pointer px-3 py-2 bg-amber-50 text-amber-900 text-xs font-medium">
                  No entran al lote ({preview.excludedCount}) — exclusiones automáticas
                </summary>
                <div className="overflow-auto max-h-64">
                  <table className="min-w-full text-xs">
                    <thead className="bg-amber-50/60 sticky top-0 text-left">
                      <tr>
                        <th className="px-2 py-2">Cliente</th>
                        <th className="px-2 py-2">Crédito</th>
                        <th className="px-2 py-2">Cuota</th>
                        <th className="px-2 py-2">Vence</th>
                        <th className="px-2 py-2 text-right">Importe</th>
                        <th className="px-2 py-2">Motivo</th>
                        <th className="px-2 py-2">Lote activo</th>
                      </tr>
                    </thead>
                    <tbody>
                      {preview.excluded.map((e) => (
                        <tr key={e.installmentId} className="border-t">
                          <td className="px-2 py-1.5 font-medium text-slate-800">
                            {e.customerName ?? '—'}
                          </td>
                          <td className="px-2 py-1.5 font-mono">{e.loanNumber ?? '—'}</td>
                          <td className="px-2 py-1.5">
                            {e.installmentNumber != null ? `#${e.installmentNumber}` : '—'}
                          </td>
                          <td className="px-2 py-1.5">
                            {e.dueDate ? dateLabel(e.dueDate) : '—'}
                          </td>
                          <td className="px-2 py-1.5 text-right tabular-nums">
                            {e.amount != null ? `$${money(e.amount)}` : '—'}
                          </td>
                          <td className="px-2 py-1.5 text-amber-900">{e.reason}</td>
                          <td className="px-2 py-1.5 font-mono text-slate-600">
                            {e.activeBatchNumber
                              ? `${e.activeBatchNumber}${e.activeBatchStatus ? ` (${e.activeBatchStatus})` : ''}`
                              : '—'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {preview.excludedCount > preview.excluded.length && (
                  <p className="px-3 py-2 text-[11px] text-amber-800 border-t bg-amber-50/40">
                    Mostrando {preview.excluded.length} de {preview.excludedCount} exclusiones.
                  </p>
                )}
              </details>
            )}
          </div>
        )}
      </section>

      <section className="rounded-lg border bg-white overflow-hidden">
        <div className="px-4 py-3 border-b font-medium flex flex-wrap items-center justify-between gap-2">
          <span>Lotes</span>
          <label className="text-xs font-normal flex items-center gap-2">
            <input
              type="checkbox"
              checked={filterReview}
              onChange={(e) => setFilterReview(e.target.checked)}
            />
            Requiere revisión
          </label>
        </div>
        <table className="min-w-full text-sm">
          <thead className="bg-gray-50 text-left">
            <tr>
              <th className="px-3 py-2">Nº</th>
              <th className="px-3 py-2">Sucursal</th>
              <th className="px-3 py-2">Estado</th>
              <th className="px-3 py-2">Líneas</th>
              <th className="px-3 py-2">Monto</th>
              <th className="px-3 py-2">Pagados</th>
              <th className="px-3 py-2">Rechazados</th>
              <th className="px-3 py-2">Revisión</th>
              <th className="px-3 py-2">Enviado</th>
              <th className="px-3 py-2" />
            </tr>
          </thead>
          <tbody>
            {batches.map((b: any) => (
              <tr key={b.id} className="border-t">
                <td className="px-3 py-2 font-mono text-xs">{b.batchNumber}</td>
                <td className="px-3 py-2">{b.branch?.code ?? '—'}</td>
                <td className="px-3 py-2">{b.status}</td>
                <td className="px-3 py-2">{b.lineCount}</td>
                <td className="px-3 py-2">
                  ${Number(b.totalAmount).toLocaleString('es-AR', { minimumFractionDigits: 2 })}
                </td>
                <td className="px-3 py-2">{b.paidCount ?? 0}</td>
                <td className="px-3 py-2">{b.rejectedCount ?? 0}</td>
                <td className="px-3 py-2">{b.reviewRequiredCount ?? 0}</td>
                <td className="px-3 py-2">{b.sentAt ? String(b.sentAt).slice(0, 10) : '—'}</td>
                <td className="px-3 py-2">
                  <button
                    type="button"
                    className="text-blue-700 underline text-xs"
                    onClick={() => openBatch(b.id)}
                  >
                    Abrir
                  </button>
                </td>
              </tr>
            ))}
            {!batches.length && (
              <tr>
                <td colSpan={10} className="px-3 py-6 text-center text-gray-500">
                  Sin lotes
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </section>

      {selected && (
        <section className="rounded-lg border bg-white p-4 space-y-3">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <h2 className="font-medium">
              {selected.batchNumber} · {selected.status}
            </h2>
            <div className="flex flex-wrap gap-2 text-sm items-center">
              {selected.hasExcel && (
                <button
                  type="button"
                  className="px-2 py-1 border rounded"
                  onClick={() => apiService.downloadBaproDebitFile(selected.id, 'excel')}
                >
                  Excel
                </button>
              )}
              {selected.hasFixed && (
                <button
                  type="button"
                  className="px-2 py-1 border rounded"
                  onClick={() => apiService.downloadBaproDebitFile(selected.id, 'fixed')}
                >
                  EBT_
                </button>
              )}
              {(selected.status === 'SENT' || selected.status === 'PROCESSING') && (
                <>
                  <label className="px-2 py-1 border rounded cursor-pointer bg-slate-50">
                    Importar respuesta(s)
                    <input
                      type="file"
                      multiple
                      accept=".json,application/json"
                      className="hidden"
                      disabled={busy}
                      onChange={(e) => {
                        void importResponseFiles(e.target.files);
                        e.target.value = '';
                      }}
                    />
                  </label>
                  <button type="button" className="px-2 py-1 border rounded" onClick={releaseSelected}>
                    Liberar remanente
                  </button>
                  <button
                    type="button"
                    className="px-2 py-1 border rounded bg-amber-50 text-amber-900"
                    disabled={busy}
                    onClick={() => void closeSelected()}
                  >
                    Cerrar lote
                  </button>
                </>
              )}
              {selected.status === 'PROCESSED' && (
                <span className="text-xs text-slate-500 px-2 py-1">Lote cerrado — sin más imports</span>
              )}
            </div>
          </div>
          {(selected.status === 'SENT' || selected.status === 'PROCESSING') && (
            <p className="text-xs text-slate-500">
              Podés subir uno o varios archivos JSON a medida que el banco confirme débitos (días de
              cobro distintos). El lote permanece abierto hasta el 100% cobrado o hasta que pulses
              &quot;Cerrar lote&quot;.
            </p>
          )}
          {importSummary ? (
            <p className="text-xs text-slate-600">
              Último import: total {importSummary.total} · ok {importSummary.success} · rej{' '}
              {importSummary.rejected} · unmatched {importSummary.unmatched} · dup{' '}
              {importSummary.duplicates} · review {importSummary.reviewRequired}
            </p>
          ) : null}
          {Array.isArray(selected.importSummary?.history) &&
          selected.importSummary.history.length > 1 ? (
            <p className="text-xs text-slate-500">
              Imports acumulados en este lote: {selected.importSummary.history.length}
            </p>
          ) : null}
          <div className="overflow-auto max-h-80 border rounded">
            <table className="min-w-full text-xs">
              <thead className="bg-gray-50 sticky top-0">
                <tr>
                  <th className="px-2 py-1 text-left">Préstamo</th>
                  <th className="px-2 py-1 text-left">Cuota</th>
                  <th className="px-2 py-1 text-left">Cliente</th>
                  <th className="px-2 py-1 text-left">Importe</th>
                  <th className="px-2 py-1 text-left">Estado</th>
                  <th className="px-2 py-1 text-left">Código</th>
                  <th className="px-2 py-1 text-left">Pago</th>
                </tr>
              </thead>
              <tbody>
                {(selected.lines ?? []).map((l: any) => (
                  <tr key={l.id} className="border-t">
                    <td className="px-2 py-1">{l.loanNumber}</td>
                    <td className="px-2 py-1">{l.installmentNumber}</td>
                    <td className="px-2 py-1">
                      {l.customer
                        ? `${l.customer.lastName}, ${l.customer.firstName}`
                        : l.clientRef}
                    </td>
                    <td className="px-2 py-1">${Number(l.amount).toFixed(2)}</td>
                    <td className="px-2 py-1">
                      {l.status}
                      {l.needsReview ? ' · revisión' : ''}
                      {l.paymentReversedAt ? ' · pago revertido' : ''}
                    </td>
                    <td className="px-2 py-1">{l.resultCode ?? '—'}</td>
                    <td className="px-2 py-1 font-mono">
                      {l.paymentId ? l.paymentId.slice(0, 8) + '…' : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {(selected.importIncidents ?? []).length > 0 ? (
            <details className="text-xs">
              <summary className="cursor-pointer text-amber-800">
                Incidentes de import ({selected.importIncidents.length})
              </summary>
              <ul className="mt-2 space-y-1 max-h-40 overflow-auto">
                {selected.importIncidents.map((inc: any) => (
                  <li key={inc.id}>
                    [{inc.kind}] {inc.message}
                  </li>
                ))}
              </ul>
            </details>
          ) : null}
        </section>
      )}
    </div>
  );
}
